import React, { useState, useEffect } from "react";
import {
  DeleteOutlined,
  UserOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  TeamOutlined,
  CheckOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  Popconfirm,
  message,
  Card,
  Typography,
  Tag,
  Button,
  Space,
  Tooltip,
  Menu,
  Dropdown,
  Progress,
  Badge,
  Modal,
  Form,
  Row,
  Col,
  InputNumber,
  Upload,
  Input,
  notification,
  Select,
} from "antd";
import { postReq, deleteApi } from "../api";
import { Link } from "react-router-dom";
import moment from "moment";
import { toastNotification } from "../utils/toasthelper";
import useUserDetails from "../utils/useUserDetails";
import TextArea from "antd/lib/input/TextArea";
const { Option } = Select;

const { Title, Text } = Typography;

/* ─────────────────────────────────────────────
   Helper utilities
───────────────────────────────────────────── */
const pad = (n) => n.toString().padStart(2, "0");

function formatCountdown(ms) {
  if (ms <= 0) return "Expired";
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${pad(m)}m ${pad(s)}s`;
  return `${pad(m)}m ${pad(s)}s`;
}

function getInitials(name = "") {
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();
}

/* Returns { label, state: 'ok'|'warning'|'urgent'|'sent', percent } */
function getDeadlineInfo(createdAt, sendDeadlineHours, candidateSentAt) {
  if (candidateSentAt) return { label: "Sent", state: "sent", percent: 100 };
  const now = Date.now();
  const created = new Date(createdAt).getTime();
  const totalMs = sendDeadlineHours * 3_600_000;
  const deadline = created + totalMs;
  const remainMs = deadline - now;
  const elapsedMs = now - created;
  const percent = Math.min(100, Math.round((elapsedMs / totalMs) * 100));
  const label = formatCountdown(remainMs);
  if (remainMs <= 0) return { label: "Expired", state: "urgent", percent: 100 };
  if (remainMs < 3_600_000) return { label, state: "urgent", percent };
  if (remainMs < 7_200_000) return { label, state: "warning", percent };
  return { label, state: "ok", percent };
}

/* Returns { label, state: 'ok'|'warning'|'overdue', timeLabel } */
function getFollowupInfo(followupDeadline) {
  if (!followupDeadline) return null;
  const ms = new Date(followupDeadline).getTime() - Date.now();
  const h = ms / 3_600_000;
  if (ms <= 0)
    return {
      state: "overdue",
      label: "Follow-up overdue!",
      timeLabel: `${Math.abs(Math.round(h))}h ago`,
    };
  if (h < 4)
    return {
      state: "warning",
      label: "Follow-up due soon",
      timeLabel: `in ${formatCountdown(ms)}`,
    };
  return {
    state: "ok",
    label: "Follow-up on schedule",
    timeLabel: `in ${Math.round(h)}h`,
  };
}

// function getSlotState(deadline, existing) {
//   if (existing?.completedAt) return "done";
//   const ms = deadline - Date.now();
//   if (ms <= 0) return "overdue";
//   if (ms < 4 * 3_600_000) return "due-soon";
//   return "pending";
// }

/* ─────────────────────────────────────────────
   Inline style tokens
───────────────────────────────────────────── */
const HEADER_COLORS = {
  remote: "#1565C0",
  onsite: "#0D3B68",
  hybrid: "#0B5A3E",
};

const DEADLINE_COLORS = {
  sent: { bg: "#E6F1FB", color: "#185FA5", border: "#B5D4F4", bar: "#378ADD" },
  ok: { bg: "#EAF3DE", color: "#3B6D11", border: "#C0DD97", bar: "#639922" },
  warning: {
    bg: "#FAEEDA",
    color: "#854F0B",
    border: "#FAC775",
    bar: "#BA7517",
  },
  urgent: {
    bg: "#FCEBEB",
    color: "#A32D2D",
    border: "#F7C1C1",
    bar: "#E24B4A",
  },
};

const SLOT_STYLES = {
  done: { bg: "#EAF3DE", border: "#C0DD97", text: "#3B6D11", dot: "#639922" },
  pending: { bg: "#f8f9fa", border: "#e0e0e0", text: "#777", dot: "#ccc" },
  "due-soon": {
    bg: "#FAEEDA",
    border: "#FAC775",
    text: "#854F0B",
    dot: "#BA7517",
  },
  overdue: {
    bg: "#FCEBEB",
    border: "#E24B4A",
    text: "#A32D2D",
    dot: "#E24B4A",
  },
};

const FOLLOWUP_COLORS = {
  ok: { bg: "#EAF3DE", border: "#C0DD97", text: "#3B6D11", dot: "#639922" },
  warning: {
    bg: "#FAEEDA",
    border: "#FAC775",
    text: "#854F0B",
    dot: "#BA7517",
  },
  overdue: {
    bg: "#FCEBEB",
    border: "#E24B4A",
    text: "#A32D2D",
    dot: "#E24B4A",
  },
};

const STATUS_COLORS = {
  open: { border: "#69f0ae", color: "#69f0ae" },
  "on-hold": { border: "#ffd54f", color: "#ffd54f" },
  close: { border: "#ef9a9a", color: "#ef9a9a" },
};

/* ─────────────────────────────────────────────
   Sub-components
───────────────────────────────────────────── */
function ClientAvatar({ name }) {
  return (
    <div
      style={{
        width: 32,
        height: 32,
        borderRadius: 8,
        background: "#E6F1FB",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 11,
        fontWeight: 600,
        color: "#185FA5",
        flexShrink: 0,
        userSelect: "none",
      }}
    >
      {getInitials(name)}
    </div>
  );
}

function FieldItem({ label, value, mono = false }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span
        style={{
          fontSize: 10,
          fontWeight: 600,
          color: "#888",
          textTransform: "uppercase",
          letterSpacing: "0.6px",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 12.5,
          color: "#333",
          fontWeight: mono ? 500 : 400,
          fontFamily: mono ? "monospace" : "inherit",
        }}
      >
        {value}
      </span>
    </div>
  );
}

function CountdownChip({ label, state }) {
  const c = DEADLINE_COLORS[state] || DEADLINE_COLORS.ok;
  return (
    <span
      style={{
        fontFamily: "monospace",
        fontSize: 11,
        fontWeight: 500,
        padding: "3px 9px",
        borderRadius: 6,
        background: c.bg,
        color: c.color,
        border: `1px solid ${c.border}`,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

function DeadlineBar({ percent, state }) {
  const c = DEADLINE_COLORS[state] || DEADLINE_COLORS.ok;
  return (
    <div
      style={{
        height: 4,
        background: "#eee",
        borderRadius: 4,
        overflow: "hidden",
        margin: "8px 0 6px",
      }}
    >
      <div
        style={{
          height: "100%",
          width: `${percent}%`,
          background: c.bar,
          borderRadius: 4,
          transition: "width 1s linear",
        }}
      />
    </div>
  );
}

function FollowupStrip({ info }) {
  if (!info) return null;
  const c = FOLLOWUP_COLORS[info.state] || FOLLOWUP_COLORS.ok;
  return (
    <div
      style={{
        marginTop: 8,
        padding: "8px 12px",
        borderRadius: 10,
        display: "flex",
        alignItems: "center",
        gap: 8,
        background: c.bg,
        border: `0.5px solid ${c.border}`,
      }}
    >
      {/* Blinking dot for overdue */}
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: c.dot,
          flexShrink: 0,
          display: "inline-block",
          animation: info.state === "overdue" ? "blink 1.2s infinite" : "none",
        }}
      />
      <span style={{ fontSize: 11, fontWeight: 500, flex: 1, color: c.text }}>
        {info.label}
      </span>
      <span
        style={{
          fontSize: 10.5,
          fontFamily: "monospace",
          fontWeight: 500,
          color: c.text,
        }}
      >
        {info.timeLabel}
      </span>
      <style>{`@keyframes blink { 0%,100%{opacity:1} 50%{opacity:0.3} }`}</style>
    </div>
  );
}

function getSlotState(followup) {
  if (followup.completedAt) return "done";
  const ms = new Date(followup.dueAt) - Date.now();
  if (ms <= 0) return "overdue";
  if (ms < 4 * 3_600_000) return "due-soon";
  return "pending";
}

function FollowupSlot({ slot, onMarkDone }) {
  const state = getSlotState(slot);
  const s = SLOT_STYLES[state];
  const msLeft = new Date(slot.dueAt) - Date.now();
  console.log("state", state, state === "done");
  const timeLabel =
    state === "done"
      ? `Done · ${moment(slot.completedAt).format("DD/MM,hh:mm A")}`
      : state === "overdue"
        ? `${Math.abs(Math.floor(msLeft / 3_600_000))}h overdue`
        : formatCountdown(msLeft);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 10px",
        borderRadius: 8,
        background: s.bg,
        border: `0.5px solid ${s.border}`,
        marginBottom: 6,
      }}
    >
      {/* Blinking dot */}
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: s.dot,
          flexShrink: 0,
          display: "inline-block",
          animation: state === "overdue" ? "fu-blink 1.2s infinite" : "none",
        }}
      />

      {/* Left: label + due date + note */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: s.text }}>
          {slot.label}
        </div>
        <div style={{ fontSize: 10, color: s.text, opacity: 0.75 }}>
          Due: {moment(slot.dueAt).format("DD MMM YYYY, hh:mm A")}
        </div>
        <Tooltip title={slot.note}>
          {slot?.note && (
            <div
              style={{
                fontSize: 10.5,
                color: "#555",
                marginTop: 2,
                fontStyle: "italic",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "100%",
              }}
            >
              "{slot.note}"
            </div>
          )}
        </Tooltip>
      </div>

      {/* Timer chip */}
      <span
        style={{
          fontSize: 10,
          fontFamily: "monospace",
          fontWeight: 600,
          flexShrink: 0,
          padding: "2px 7px",
          borderRadius: 6,
          background: "rgba(255,255,255,0.7)",
          color: s.text,
          border: `1px solid ${s.border}`,
        }}
      >
        {timeLabel}
      </span>

      {/* Mark-done button (hidden when done) */}
      {state !== "done" && (
        <button
          onClick={() => onMarkDone(slot.index)}
          title="Mark as done"
          style={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: 3,
            background: "#fff",
            border: `1px solid ${s.border}`,
            borderRadius: 6,
            cursor: "pointer",
            padding: "3px 8px",
            fontSize: 11,
            fontWeight: 700,
            color: s.text,
          }}
        >
          <CheckOutlined style={{ fontSize: 10 }} /> Done
        </button>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Main CardComponent
   
   NEW PROPS (add these to your API response or pass as defaults):
     sendDeadlineHours  – number, hours allowed to send first candidate (default 3)
     candidateSentAt    – ISO date string or null
     followupDeadline   – ISO date string for follow-up reminder
     candidatesShared   – number of candidates already shared
     totalRequired      – total candidates needed
     clientSub          – sub-label under client name (e.g. "Staffing Division")
───────────────────────────────────────────── */
const CardComponent = ({
  requirementName,
  jobId,
  requirementType,
  maxExperience,
  minExperience,
  jobLocation,
  requirementNiche,
  requirementStatus,
  workType,
  vacancy,
  budget,
  data,
  fetch,
  client,
  candidates,
  /* ── new props ── */
  candidate = [],
  followups = [],
  /* ── new props ── */
  sendDeadlineHours = 3,
  candidateSentAt = null,
  followupDeadline = moment(data.createdAt).add(1, "days"),
  candidatesShared = 0,
  totalRequired = 1,
}) => {
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0); // forces re-render every second
  const [showFollowups, setShowFollowups] = useState(false);
  const [followupModal, setFollowupModal] = useState(null); // { index }
  const [noteText, setNoteText] = useState("");
  const [noteLoading, setNoteLoading] = useState(false);
  const [showApplyModal, setshowApplyModal] = useState(false);
  const [appliedResume, setAppliedResume] = useState(null);

  const [form] = Form.useForm();
  const userData = useUserDetails();

  const handleApplySubmit = async (values) => {
    try {
      const formData = new FormData();

      Object.entries(values).forEach(([key, val]) => {
        if (Array.isArray(val)) {
          formData.append(key, JSON.stringify(val));
        } else {
          formData.append(key, val);
        }
      });
      formData.append("requirementId", data._id);
      if (appliedResume) {
        formData.append("resume", appliedResume);
      }
      let postData;
      postData =
        userData?.userRole == "company"
          ? await postReq("/company/candidate/requirement", formData, {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            })
          : userData.permissions.includes("apply_candidate")
            ? await postReq("/common/candidate/requirement", formData, {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              })
            : { status: 201 };

      if (postData.status == 200) {
        // fetchData();
        form.resetFields();
        setshowApplyModal(false);
        setAppliedResume(null);
        notification.success({
          message: "Success",
          description: `The Candidate attach to requirement successful.`,
          placement: "topRight",
        });
      } else {
        form.resetFields();
        setshowApplyModal(false);
        setAppliedResume(null);
        notification.error({
          message: "Error",
          description:
            postData.message ||
            `There was an error attching Candidate to requirement.`,
          placement: "topRight",
        });
      }
    } catch (error) {
      console.log("error");
      notification.error({
        message: "Error",
        description:
          error.message || "There was an error processing your request.",
        placement: "topRight",
      });
      setAppliedResume(null);
      form.resetFields();
      // setShowModal(false);
    }
  };

  // Live countdown — re-render every second
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const { userRole, permissions } = useUserDetails();

  const firstTwoTechnology = requirementNiche.slice(0, 2);
  const remainingTechnologyCount = requirementNiche.length - 2;
  const remainingTechnology = requirementNiche.slice(2);

  /* ── computed values ── */
  const deadlineInfo = getDeadlineInfo(
    data.createdAt,
    sendDeadlineHours,
    candidate.length > 0,
  );
  const followupInfo = getFollowupInfo(followupDeadline);
  const deadlineTimestamp = new Date(
    new Date(data.createdAt).getTime() + sendDeadlineHours * 3_600_000,
  );
  const headerBg = HEADER_COLORS[workType] || HEADER_COLORS.onsite;
  const statusColors = STATUS_COLORS[requirementStatus] || {};

  // const followupSchedule = buildFollowupSchedule(
  //   (candidateSentAt = candidate.length > 0 ? candidate[0]?.createdAt : null),
  //   followups,
  // );
  const doneCount = followups.filter((f) => f.completedAt).length;
  const overdueCount = followups.filter(
    (f) => !f.completedAt && new Date(f.dueAt) < new Date(),
  ).length;

  const badgeStyle =
    overdueCount > 0
      ? { bg: "#FCEBEB", color: "#A32D2D", border: "#F7C1C1" }
      : doneCount === 3
        ? { bg: "#EAF3DE", color: "#3B6D11", border: "#C0DD97" }
        : { bg: "#E6F1FB", color: "#185FA5", border: "#B5D4F4" };

  const handleMarkDone = (index) => {
    setNoteText("");
    setFollowupModal({ index });
  };

  const handleConfirmDone = async () => {
    // if (!onFollowupDone) {
    //   message.warning("onFollowupDone handler not provided");
    //   setFollowupModal(null);
    //   return;
    // }
    setNoteLoading(true);
    try {
      const getRes = await postReq("company/requirement/followupDone", {
        requirementId: data._id,
        index: followupModal.index,
        noteText,
      });
      if (getRes.status == 1) {
        message.success(getRes?.message || "SUCCESS");
      } else {
        message.warning(getRes?.message || "ERROR");
      }
    } catch {
      message.error("Failed to save follow-up");
    }
    setNoteLoading(false);
    setFollowupModal(null);
  };

  /* ── API calls ── */
  const updateStatus = async (newStatus) => {
    setLoading(true);
    const endpoint =
      userRole === "company"
        ? "company/requirement/update-status"
        : "common/requirement/update-status";
    const canUpdate =
      userRole === "company" ||
      permissions.includes("update_requirement_status");
    const response = canUpdate
      ? await postReq(endpoint, {
          requirementId: data._id,
          clientId: data.clientId,
          requirementStatus: newStatus,
        })
      : { status: 0 };
    if (response.status === 1) fetch();
    setLoading(false);
  };

  const deleteRequirementAPI = async () => {
    try {
      const res = await deleteApi(
        `company/requirement/${data._id}`,
        {},
        "DELETE",
      );
      if (res.data.status === 1) {
        toastNotification("success", "Requirement deleted successfully");
        fetch();
      } else {
        toastNotification(
          "error",
          res.message || "Unable to delete requirement",
        );
      }
    } catch (err) {
      console.error(err);
      message.error("Server error");
    }
  };

  const statusMenu = (
    <Menu
      onClick={async ({ key }) => updateStatus(key)}
      items={[
        { key: "open", label: "OPEN" },
        { key: "on-hold", label: "HOLD" },
        { key: "close", label: "CLOSE" },
      ]}
    />
  );

  /* ── render ── */
  return (
    <>
      <Modal
        open={!!followupModal}
        title={`Mark Follow-up ${followupModal?.index} as Done`}
        onCancel={() => setFollowupModal(null)}
        footer={[
          <Button key="cancel" onClick={() => setFollowupModal(null)}>
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            loading={noteLoading}
            onClick={handleConfirmDone}
          >
            Confirm
          </Button>,
        ]}
      >
        <p style={{ fontSize: 13, color: "#555", marginBottom: 8 }}>
          Add a note about what was discussed in this follow-up (optional):
        </p>
        <TextArea
          rows={3}
          placeholder="e.g. Candidate Shortlisted for further screening..."
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
        />
      </Modal>
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
        }}
      >
        {/* ── HEADER ── */}
        <div
          style={{
            background: headerBg,
            padding: "14px 16px 12px",
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            {/* Title block */}
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: "#fff",
                  lineHeight: 1.3,
                }}
              >
                {requirementName}
              </div>
              <div
                style={{
                  fontFamily: "monospace",
                  fontSize: 11,
                  color: "rgba(255,255,255,0.6)",
                  marginTop: 2,
                  letterSpacing: "0.5px",
                }}
              >
                {jobId}
              </div>
            </div>

            {/* Actions */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexShrink: 0,
              }}
            >
              {permissions.includes("delete_requirements") && (
                <Popconfirm
                  title="Delete Requirement"
                  description="Are you sure you want to delete this requirement?"
                  okText="Yes"
                  cancelText="No"
                  onConfirm={deleteRequirementAPI}
                >
                  <DeleteOutlined
                    style={{
                      fontSize: 14,
                      color: "rgba(255,255,255,0.7)",
                      cursor: "pointer",
                    }}
                  />
                </Popconfirm>
              )}

              <Dropdown
                overlay={statusMenu}
                trigger={["click"]}
                disabled={!permissions.includes("update_requirement_status")}
              >
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.8px",
                    padding: "3px 9px",
                    borderRadius: 20,
                    cursor: "pointer",
                    border: `1.5px solid ${statusColors.border || "rgba(255,255,255,0.4)"}`,
                    color: statusColors.color || "#fff",
                    background: "rgba(255,255,255,0.12)",
                    textTransform: "uppercase",
                    userSelect: "none",
                  }}
                >
                  {requirementStatus === "on-hold"
                    ? "HOLD"
                    : requirementStatus?.toUpperCase()}
                </span>
              </Dropdown>
            </div>
          </div>

          {/* Work-type badge */}
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontSize: 10,
              fontWeight: 500,
              color: "rgba(255,255,255,0.8)",
              background: "rgba(255,255,255,0.12)",
              borderRadius: 6,
              padding: "2px 7px",
              width: "fit-content",
            }}
          >
            {workType === "remote" ? "↻" : workType === "hybrid" ? "⇌" : "●"}
            &nbsp;
            {workType?.charAt(0).toUpperCase() + workType?.slice(1)} ·{" "}
            {jobLocation}
          </span>
        </div>

        {/* ── BODY ── */}
        <div
          style={{
            padding: "14px 16px",
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Client row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              paddingBottom: 12,
              borderBottom: "1px solid #f0f0f0",
              marginBottom: 12,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              <ClientAvatar name={client?.clientCompanyName} />
              <div style={{ fontSize: 13, fontWeight: 600, color: "#222" }}>
                {client?.clientCompanyName}
              </div>
            </div>
            {(userData?.userRole == "company" ||
              userData?.permissions?.includes("apply_candidate")) && (
              <Button
                key="confirm"
                type="ghost"
                loading={noteLoading}
                onClick={() => setshowApplyModal(true)}
              >
                + Candidate
              </Button>
            )}
          </div>

          {/* Fields grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "10px 16px",
              marginBottom: 12,
            }}
          >
            <FieldItem
              label="Vacancy"
              value={`${vacancy} position${vacancy > 1 ? "s" : ""}`}
            />
            <FieldItem
              label="Experience"
              value={`${minExperience} – ${maxExperience} yrs`}
            />
            <FieldItem label="Job Type" value={requirementType} />
            <FieldItem label="Budget" value={`${budget} +GST`} mono />
            <FieldItem
              label="Created At"
              value={moment(data.createdAt).format("DD MMM YY, hh:mm A")}
            />
            <FieldItem
              label="Candidate Sent"
              value={
                candidate?.length > 0
                  ? moment(candidate[0].createdAt).format("DD MMM YY, hh:mm A")
                  : "—"
              }
            />
          </div>

          {/* Skills */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 5,
              paddingTop: 10,
              borderTop: "1px solid #f0f0f0",
              marginBottom: 12,
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: "#555",
                marginRight: 2,
              }}
            >
              Skills:
            </span>
            {firstTwoTechnology.map((skill, i) => (
              <Tag
                key={i}
                style={{
                  borderRadius: 20,
                  color: "#185FA5",
                  fontWeight: 600,
                  border: "1.5px solid #B5D4F4",
                  background: "#E6F1FB",
                  fontSize: 11,
                  margin: 0,
                }}
              >
                {skill?.value}
              </Tag>
            ))}
            {remainingTechnologyCount > 0 && (
              <Tooltip
                placement="top"
                title={
                  <div style={{ maxWidth: 200 }}>
                    {remainingTechnology.map((skill, i) => (
                      <p key={i} style={{ fontSize: 12, margin: "2px 0" }}>
                        {skill.value}
                      </p>
                    ))}
                  </div>
                }
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    padding: "2px 8px",
                    borderRadius: 20,
                    background: "#378ADD",
                    color: "#fff",
                    cursor: "pointer",
                  }}
                >
                  +{remainingTechnologyCount}
                </span>
              </Tooltip>
            )}
          </div>

          {/* ── TIMELINE SECTION ── */}
          <div
            style={{
              background: "#f8f9fa",
              borderRadius: 10,
              border: "1px solid #eee",
              padding: "10px 12px",
              marginBottom: 8,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#999",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                {candidate.length > 0
                  ? "Candidates shared"
                  : "Send candidate by"}
              </span>
              <CountdownChip
                label={deadlineInfo.label}
                state={deadlineInfo.state}
              />
            </div>

            <DeadlineBar
              percent={deadlineInfo.percent}
              state={deadlineInfo.state}
            />

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 10.5,
                color: "#aaa",
              }}
            >
              <span>
                {candidate.length > 0
                  ? `${candidate.length} of ${vacancy} sent`
                  : `Posted: ${moment(data.createdAt).format("DD MMM YY, hh:mm A")}`}
              </span>
              <span>
                {candidate.length > 0
                  ? `✓ ${moment(candidate[0].createdAt).format("DD MMM YY, hh:mm A")}`
                  : `Deadline: ${moment(deadlineTimestamp).format("DD MMM YY, hh:mm A")}`}
              </span>
            </div>
          </div>

          {/* ── FOLLOW-UP REMINDER ── */}
          {/* <FollowupStrip info={followupInfo} /> */}

          {followups.length > 0 ? (
            <div
              style={{
                borderRadius: 10,
                border: "1px solid #eee",
                overflow: "hidden",
              }}
            >
              {/* Collapsible header */}
              <button
                onClick={() => setShowFollowups((v) => !v)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "9px 12px",
                  background: "#f8f9fa",
                  border: "none",
                  cursor: "pointer",
                  borderBottom: showFollowups ? "1px solid #eee" : "none",
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#555",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Follow-up Tracker
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      padding: "2px 8px",
                      borderRadius: 20,
                      background: badgeStyle.bg,
                      color: badgeStyle.color,
                      border: `1px solid ${badgeStyle.border}`,
                    }}
                  >
                    {doneCount}/3 done
                    {overdueCount > 0 ? ` · ${overdueCount} overdue` : ""}
                  </span>
                  <span style={{ fontSize: 11, color: "#bbb" }}>
                    {showFollowups ? "▲" : "▼"}
                  </span>
                </div>
              </button>

              {/* Slots — shown when expanded */}
              {showFollowups && (
                <div style={{ padding: "10px 10px 6px" }}>
                  {followups.map((slot) => (
                    <FollowupSlot
                      key={slot.index}
                      slot={slot}
                      onMarkDone={handleMarkDone}
                    />
                  ))}
                  <div
                    style={{
                      fontSize: 10,
                      color: "#ccc",
                      textAlign: "center",
                      marginTop: 4,
                      paddingBottom: 2,
                    }}
                  >
                    Each follow-up is 24h after the previous · 3 total
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Placeholder when candidate not sent yet */
            <div
              style={{
                padding: "8px 12px",
                borderRadius: 10,
                background: "#f8f9fa",
                border: "1px dashed #ddd",
                fontSize: 11,
                color: "#bbb",
                textAlign: "center",
              }}
            >
              Follow-up tracker activates after first candidate is sent
            </div>
          )}
        </div>

        {/* ── CLICK AREA → Detail Page ── */}
        <Link
          to={{ pathname: "/company/requirement_description", state: { data } }}
          style={{
            display: "block",
            padding: "10px 16px",
            borderTop: "1px solid #f0f0f0",
            fontSize: 12,
            color: "#378ADD",
            fontWeight: 500,
            textAlign: "center",
            textDecoration: "none",
            background: "#fff",
            borderRadius: "0 0 16px 16px",
            transition: "background 0.15s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#E6F1FB")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
        >
          View Details →
        </Link>
      </div>

      <Modal
        title={`Apply Candidate`}
        open={showApplyModal}
        onCancel={() => {
          setshowApplyModal(false);
          // setEditData(null);
        }}
        footer={
          <div style={{ paddingTop: "10px" }}>
            <Button
              type="default"
              // onClick={handleClear}
              style={{ marginRight: 8 }}
            >
              Reset
            </Button>
            <Button type="submit" onClick={() => form.submit()}>
              Submit
            </Button>
          </div>
        }
        width="50%" // Full width on moblepx
        style={{ top: 50 }}
        bodyStyle={{ padding: 10 }}
        responsive={true}
        className="responsive-modal"
      >
        <Form form={form} layout="vertical" onFinish={handleApplySubmit}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Select Candidate"
                name="candidateId"
                rules={[
                  {
                    required: true,
                    message: "Please select requirement type",
                  },
                ]}
              >
                <Select
                  // mode="multiple"
                  placeholder="Select"
                  className="ant-select-selection1"
                  allowClear
                  maxTagCount="responsive"
                  searchValue=""
                >
                  {candidates.length > 0 &&
                    candidates.map((data, i) => (
                      <Option key={i} value={`${data.value}`}>
                        {data.label}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Monthly Budget"
                name="budget"
                rules={[
                  {
                    required: true,
                    message: "Please enter monthly budget",
                  },
                ]}
              >
                <InputNumber
                  maxLength={7}
                  placeholder="Enter"
                  style={{ width: "100%" }}
                  min={0}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={24}>
              <Form.Item label="Description." name="description">
                <Input.TextArea
                  maxLength={4000}
                  style={{ height: 100, resize: "vertical" }}
                  placeholder="Enter"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Resume"
                rules={[{ required: true, message: "Resume is required." }]}
              >
                <Upload
                  accept=".pdf,.doc,.docx"
                  maxCount={1}
                  beforeUpload={() => false} // prevent auto upload
                  onChange={(info) => {
                    const file = info.fileList[0]?.originFileObj;
                    setAppliedResume(file || null);
                  }}
                >
                  <Button icon={<UploadOutlined />}>Upload Resume</Button>
                </Upload>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default CardComponent;
// import React, { useState } from "react";
// import { DeleteOutlined } from "@ant-design/icons";
// import {
//   Popconfirm,
//   message,
//   Card,
//   Typography,
//   Tag,
//   Button,
//   Space,
//   Tooltip,
//   Switch,
//   Segmented,
//   Menu,
//   Dropdown,
// } from "antd";
// import { postReq, deleteApi } from "../api";
// import { Link } from "react-router-dom";
// import moment from "moment";
// import { toastNotification } from "../utils/toasthelper";
// import useUserDetails from "../utils/useUserDetails";
// const { Title, Text } = Typography;

// const CardComponent = ({
//   requirementName,
//   jobId,
//   requirementType,
//   maxExperience,
//   minExperience,
//   jobLocation,
//   requirementNiche,
//   requirementStatus,
//   workType,
//   vacancy,
//   budget,
//   data,
//   fetch,
//   client,
// }) => {
//   const [loading, setLoading] = useState(false);
//   const firstTwoTechnology = requirementNiche.slice(0, 2);
//   const remainingTechnologyCount = requirementNiche.length - 2;
//   const remainingTechnology = requirementNiche.slice(
//     2,
//     requirementNiche.length
//   );
//   const { userRole, permissions } = useUserDetails();

//   const onChange = async () => {
//     setLoading(true);
//     let response;
//     response =
//       userRole == "company"
//         ? await postReq("company/requirement/update-status", {
//             requirementId: data._id,
//             clientId: data.clientId,
//             requirementStatus:
//               data.requirementStatus == "open" ? "close" : "open",
//           })
//         : permissions.includes("update_requirement_status")
//         ? await postReq("common/requirement/update-status", {
//             requirementId: data._id,
//             clientId: data.clientId,
//             requirementStatus:
//               data.requirementStatus == "open" ? "close" : "open",
//           })
//         : { status: 0 };
//     if (response.status == 1) {
//       fetch();
//     }
//     setLoading(false);
//   };

//   const deleteRequirementAPI = async () => {
//     try {
//       const res = await deleteApi(
//         `company/requirement/${data._id}`,
//         {},
//         "DELETE"
//       );
//       if (res.data.status === 1) {
//         toastNotification("success", "Requirement deleted successfully");
//         fetch();
//       } else {
//         toastNotification(
//           "error",
//           res.message || "Unable to delete requirement"
//         );
//       }
//     } catch (err) {
//       console.error(err);
//       message.error("Server error");
//     }
//   };
//   const menu = (
//     <Menu
//       onClick={async ({ key }) => {
//         let response;
//         response =
//           userRole == "company"
//             ? await postReq("company/requirement/update-status", {
//                 requirementId: data._id,
//                 clientId: data.clientId,
//                 requirementStatus: key,
//                 // data.requirementStatus == "open" ? "close" : "open",
//               })
//             : permissions.includes("update_requirement_status")
//             ? await postReq("common/requirement/update-status", {
//                 requirementId: data._id,
//                 clientId: data.clientId,
//                 requirementStatus: key,
//                 // data.requirementStatus == "open" ? "close" : "open",
//               })
//             : { status: 0 };
//         if (response.status == 1) {
//           fetch();
//         }
//         setLoading(false);
//       }}
//       items={[
//         { key: "open", label: "OPEN" },
//         { key: "on-hold", label: "HOLD" },
//         { key: "close", label: "CLOSE" },
//       ]}
//     />
//   );

//   return (
//     <Card
//       className="cardComponent"
//       style={{
//         height: "100%",
//         boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
//         borderRadius: "20px",
//       }}
//       title={
//         <div
//           style={{
//             display: "flex",
//             justifyContent: "space-between",
//             alignItems: "center",
//             background: `${
//               data?.workType == "remote" ? "#3699FF" : "#13487D"
//             }  `, //
//             color: "black",
//             padding: "18px 12px",
//             borderRadius: "20px 20px 0px 0px",
//             margin: "0px , -14px",
//           }}
//         >
//           <div>
//             <Title
//               level={4}
//               style={{
//                 marginBottom: 0,
//                 fontSize: "16px",
//                 fontWeight: "500",
//                 color: "white",
//               }}
//             >
//               {requirementName}
//               {" - "}
//               {jobId}
//             </Title>
//           </div>

//           <div>
//             <Space direction="middle">
//               {permissions.includes("delete_requirements") && (
//                 <Popconfirm
//                   title="Delete Requirement"
//                   description="Are you sure you want to delete this requirement?"
//                   okText="Yes"
//                   cancelText="No"
//                   onConfirm={deleteRequirementAPI}
//                 >
//                   <DeleteOutlined
//                     style={{
//                       fontSize: 18,
//                       color: "white",
//                       cursor: "pointer",
//                       marginRight: 8,
//                     }}
//                   />
//                 </Popconfirm>
//               )}
//               {/* <Switch
//                 checkedChildren="OPEN"
//                 unCheckedChildren="CLOSE"
//                 checked={data.requirementStatus == "open" ? true : false}
//                 onChange={onChange}
//                 loading={loading}
//                 disabled={!permissions.includes("update_requirement_status")}
//               /> */}
//               {/* <Segmented
//                 size="small"
//                 options={[
//                   { label: "OPEN", value: "open" },
//                   { label: "HOLD", value: "on-hold" },
//                   { label: "CLOSE", value: "close" },
//                 ]}
//                 value={data.requirementStatus}
//                 disabled={!permissions.includes("update_requirement_status")}
//                 onChange={async (value) => {
//                   setLoading(true);

//                   const response =
//                     userRole === "company"
//                       ? await postReq("company/requirement/update-status", {
//                           requirementId: data._id,
//                           clientId: data.clientId,
//                           requirementStatus: value,
//                         })
//                       : permissions.includes("update_requirement_status")
//                       ? await postReq("common/requirement/update-status", {
//                           requirementId: data._id,
//                           clientId: data.clientId,
//                           requirementStatus: value,
//                         })
//                       : { status: 0 };

//                   if (response.status === 1) fetch();
//                   setLoading(false);
//                 }}
//               /> */}
//               <Dropdown overlay={menu} trigger={["click"]}>
//                 <Tag
//                   // color={
//                   //   data.requirementStatus === "open"
//                   //     ? "green"
//                   //     : data.requirementStatus === "on-hold"
//                   //     ? "gold"
//                   //     : "red"
//                   // }
//                   style={{ cursor: "pointer", borderRadius: "8px" }}
//                 >
//                   {data.requirementStatus.toUpperCase()}
//                 </Tag>
//               </Dropdown>
//             </Space>
//           </div>
//         </div>
//       }
//     >
//       <Link
//         to={{
//           pathname: "/company/requirement_description",
//           state: {
//             data: data,
//           },
//         }}
//       >
//         <div style={{ marginTop: 1 }}>
//           {/* <Tag
//           color="#E7F1FC"
//           style={{
//             border: "2px solid #C1D6EE",
//             borderRadius: "16px",
//             padding: "2px 10px",
//             fontSize: "12px",
//           }}
//         >
//           <span style={{ color: "#13487D", fontWeight: "600" }}>
//             Job ID {jobId}
//           </span>
//         </Tag> */}
//           <div style={{ marginTop: 6, fontSize: "12px" }}>
//             <Text>
//               <strong>Name :</strong>
//               <span style={{ color: "#666666" }}>
//                 {" "}
//                 {client.clientCompanyName}
//               </span>
//             </Text>
//           </div>
//           <div style={{ marginTop: 12, fontSize: "12px" }}>
//             <Text>
//               <strong>Vacancy :</strong>
//               <span style={{ color: "#666666" }}> {vacancy}</span>
//             </Text>
//           </div>
//           <div style={{ marginTop: 12, fontSize: "12px" }}>
//             <Text>
//               <strong>Experience :</strong>
//               <span
//                 style={{ color: "#666666" }}
//               >{` ${minExperience} to ${maxExperience} years`}</span>
//             </Text>
//           </div>
//           <div style={{ marginTop: 12, fontSize: "12px" }}>
//             <Text>
//               <strong>Job Type :</strong>
//               <span style={{ color: "#666666" }}> {requirementType}</span>
//             </Text>
//           </div>

//           {/* Experience */}

//           {/* Location */}
//           <div style={{ marginTop: 12, fontSize: "12px" }}>
//             <Text>
//               <strong>Location :</strong>

//               <span style={{ color: "#666666" }}>
//                 {jobLocation} | {workType}
//               </span>
//             </Text>
//           </div>

//           <div style={{ marginTop: 12, fontSize: "12px" }}>
//             <Text>
//               <strong>Budget :</strong>
//               <span style={{ color: "#666666" }}>
//                 {" "}
//                 {budget} <span style={{ fontSize: "10px" }}>+GST.</span>
//               </span>
//             </Text>
//           </div>
//           <div style={{ marginTop: 12, fontSize: "12px" }}>
//             <Text>
//               <strong>Date :</strong>
//               <span style={{ color: "#666666" }}>
//                 {moment(data.createdAt).format("DD/MM/YYYY")}
//               </span>
//             </Text>
//           </div>

//           {/* Skills */}
//           <div
//             style={{
//               marginTop: 12,
//               fontSize: 12,
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "flex-start",
//             }}
//           >
//             <Text
//               style={{
//                 whiteSpace: "nowrap",
//                 marginRight: "5px",
//               }}
//             >
//               <strong>Skill:</strong>
//             </Text>
//             <div
//               style={{
//                 display: "flex",
//                 flexWrap: "wrap",
//               }}
//             >
//               <div
//                 // size={"small"}
//                 style={{
//                   display: "flex",
//                   flexWrap: "wrap",
//                   gap: "0px",
//                 }}
//               >
//                 {firstTwoTechnology?.map((data) => (
//                   <Tag
//                     style={{
//                       borderRadius: "20px",
//                       color: "#13487D",
//                       fontWeight: "600",
//                       border: "2px solid #C1D6EE",
//                     }}
//                   >
//                     {data?.value}
//                   </Tag>
//                 ))}
//                 {remainingTechnologyCount > 0 && (
//                   <Tooltip
//                     placement="top"
//                     title={
//                       <div
//                         style={{
//                           gap: "4px",
//                           maxWidth: "200px",
//                           wordWrap: "break-word",
//                         }}
//                       >
//                         {remainingTechnology.map((data, i) => (
//                           <span key={i} style={{ whiteSpace: "nowrap" }}>
//                             <p style={{ fontSize: "12px", margin: 0 }}>
//                               {data.value}
//                             </p>
//                           </span>
//                         ))}
//                       </div>
//                     }
//                   >
//                     <Button
//                       type="primary"
//                       shape="circle"
//                       style={{
//                         backgroundColor: "#1A90FF",
//                         border: "none",
//                         borderRadius: "16px",
//                         width: "18px",
//                         height: "22px",
//                         fontWeight: "bold",
//                         display: "inline-flex",
//                         alignItems: "center",
//                         justifyContent: "center",
//                         boxShadow: "0px 4px 6px rgb(26,144,255,0.3)",
//                       }}
//                     >
//                       +{remainingTechnologyCount}
//                     </Button>
//                   </Tooltip>
//                 )}
//               </div>
//             </div>
//           </div>
//         </div>
//       </Link>
//     </Card>
//   );
// };

// export default CardComponent;
